import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { LifeBuoy, HeartHandshake, ShieldCheck, Lock, Send, Eye, FileText } from "lucide-react";
import AidCaseDrawer from "@/components/AidCaseDrawer";
import { toast } from "sonner";

export default function AidIntakePage() {
  const queryClient = useQueryClient();
  const [selectedCase, setSelectedCase] = useState(null);
  const [activeTab, setActiveTab] = useState("public_form"); // 'public_form' | 'case_worker'

  const [form, setForm] = useState({
    applicant_name: "",
    applicant_email: "",
    applicant_phone: "",
    household_size: 1,
    assistance_category: "RENTAL_ASSISTANCE",
    requested_amount: "",
    hardship_explanation: "",
  });

  const { data: cases = [], refetch } = useQuery({
    queryKey: ["aid-cases"],
    queryFn: async () => {
      const res = await apiClient.get("/aid-cases");
      return res.data;
    },
  });

  const submitIntakeMutation = useMutation({
    mutationFn: async (data) => {
      const res = await apiClient.post("/aid-cases/public-intake", {
        ...data,
        household_size: parseInt(data.household_size),
        requested_amount: parseFloat(data.requested_amount),
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["aid-cases"]);
      toast.success("Aid request submitted! Confidential case file initiated.");
      setForm({
        applicant_name: "",
        applicant_email: "",
        applicant_phone: "",
        household_size: 1,
        assistance_category: "RENTAL_ASSISTANCE",
        requested_amount: "",
        hardship_explanation: "",
      });
    },
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 font-sans">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <HeartHandshake className="w-7 h-7 text-indigo-600" />
            Aid-Request & Confidential Case Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Public emergency assistance intake with RBAC-guarded confidential beneficiary case files.
          </p>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-xl">
          <button
            onClick={() => setActiveTab("public_form")}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
              activeTab === "public_form" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-600"
            }`}
          >
            Public Aid Intake Form
          </button>
          <button
            onClick={() => setActiveTab("case_worker")}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
              activeTab === "case_worker" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-600"
            }`}
          >
            <Lock className="w-3.5 h-3.5 text-amber-500" />
            Case Worker Portal ({cases.length})
          </button>
        </div>
      </div>

      {activeTab === "public_form" ? (
        <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-8 space-y-6">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Emergency Aid Application Form</h2>
            <p className="text-xs text-slate-500 mt-1">
              Please submit household details. All information is strictly confidential.
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitIntakeMutation.mutate(form);
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-700">Full Name</label>
                <input
                  required
                  type="text"
                  value={form.applicant_name}
                  onChange={(e) => setForm({ ...form, applicant_name: e.target.value })}
                  className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Email Address</label>
                <input
                  required
                  type="email"
                  value={form.applicant_email}
                  onChange={(e) => setForm({ ...form, applicant_email: e.target.value })}
                  className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-700">Phone Number</label>
                <input
                  type="text"
                  value={form.applicant_phone}
                  onChange={(e) => setForm({ ...form, applicant_phone: e.target.value })}
                  className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Household Size</label>
                <input
                  required
                  type="number"
                  min="1"
                  value={form.household_size}
                  onChange={(e) => setForm({ ...form, household_size: e.target.value })}
                  className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-700">Category of Assistance</label>
                <select
                  value={form.assistance_category}
                  onChange={(e) => setForm({ ...form, assistance_category: e.target.value })}
                  className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
                >
                  <option value="RENTAL_ASSISTANCE">Rental Assistance</option>
                  <option value="FOOD_EMERGENCY">Emergency Food Aid</option>
                  <option value="UTILITIES">Utilities Relief</option>
                  <option value="MEDICAL_AID">Medical & Healthcare</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Requested Amount ($)</label>
                <input
                  required
                  type="number"
                  step="0.01"
                  value={form.requested_amount}
                  onChange={(e) => setForm({ ...form, requested_amount: e.target.value })}
                  className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs font-mono"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700">Hardship Explanation</label>
              <textarea
                rows={3}
                required
                placeholder="Describe current emergency hardship situation..."
                value={form.hardship_explanation}
                onChange={(e) => setForm({ ...form, hardship_explanation: e.target.value })}
                className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
              />
            </div>

            <button
              type="submit"
              disabled={submitIntakeMutation.isPending}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold text-xs rounded-xl shadow-sm flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              {submitIntakeMutation.isPending ? "Submitting Request..." : "Submit Aid Application"}
            </button>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center text-xs">
            <span className="font-bold text-slate-700 flex items-center gap-1.5">
              <Lock className="w-4 h-4 text-amber-500" /> Active Confidential Aid Case File Queue
            </span>
          </div>

          <div className="divide-y divide-slate-100">
            {cases.map((c) => (
              <div key={c._id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900 text-sm">{c.applicant_name}</h3>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700">
                      {c.assistance_category}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    {c.applicant_email} • Requested ${c.requested_amount?.toFixed(2)}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-slate-100 text-slate-700">
                    {c.status}
                  </span>
                  <button
                    onClick={() => setSelectedCase(c)}
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg flex items-center gap-1"
                  >
                    <Eye className="w-3.5 h-3.5" /> Open Case File
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AidCaseDrawer aidCase={selectedCase} onClose={() => setSelectedCase(null)} onRefresh={refetch} />
    </div>
  );
}
